import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeaderCompact from '@/components/common/PageHeaderCompact.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { abrirMinuta } from '@/services/cobranzas.js';
import { hasPermission } from '@/utils/permissions.js';

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

export default function AbrirMinuta() {
  const navigate = useNavigate();
  const { user, sucursal } = useAuth();
  const [formState, setFormState] = useState({
    fecha: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const canRegistrar = hasPermission(user, 'COBRANZA_REGISTRAR');

  const usuarioNombre = useMemo(
    () => user?.nombre || user?.usuario || user?.email || 'Usuario',
    [user],
  );
  const sucursalLabel = sucursal?.nombre || sucursal?.id || 'Sin sucursal';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        fecha: formState.fecha,
        usuario_id: user?.id || null,
        usuario_nombre: usuarioNombre,
        sucursal_id: sucursal?.id || '',
      };
      const data = await abrirMinuta(payload);
      navigate(`/cobranzas/minuta/${data.minuta.id}`);
    } catch (err) {
      setError(getErrorMessage(err, 'No se pudo abrir la minuta.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-5">
      <PageHeaderCompact
        title="Apertura de minuta"
        description="Registro compacto de apertura y validacion de minuta activa."
      />

      <form
        onSubmit={handleSubmit}
        className="rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.85)] p-5 shadow-sm"
      >
        {!canRegistrar ? (
          <div className="mb-4 rounded-2xl border border-[rgba(var(--danger,255_99_99),_0.4)] bg-[rgba(var(--danger,255_99_99),_0.15)] p-4 text-sm text-[rgb(var(--danger,255_99_99))]">
            No tenes permisos para abrir minutas.
          </div>
        ) : null}

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-semibold text-[rgb(var(--fg))]">Fecha</label>
            <input
              type="date"
              value={formState.fecha}
              onChange={(event) => setFormState({ ...formState, fecha: event.target.value })}
              className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-transparent px-3 py-2 text-sm text-[rgb(var(--fg))]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[rgb(var(--fg))]">Usuario</label>
            <input
              type="text"
              value={usuarioNombre}
              disabled
              className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--muted),0.2)] px-3 py-2 text-sm text-[rgb(var(--fg))]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[rgb(var(--fg))]">Sucursal</label>
            <input
              type="text"
              value={sucursalLabel}
              disabled
              className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--muted),0.2)] px-3 py-2 text-sm text-[rgb(var(--fg))]"
            />
          </div>
        </div>

        {error ? (
          <div className="mt-4 rounded-xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] px-4 py-3 text-sm text-[rgb(var(--danger))]">
            {error}
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap gap-3">
          {canRegistrar ? (
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-90 disabled:opacity-60"
            >
              {loading ? 'Abriendo...' : 'Abrir minuta'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => navigate('/cobranzas')}
            className="rounded-xl border border-[rgba(var(--border),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.2)]"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
